import { ref } from 'vue';
import { getChatBalanceCheckEnabled } from '@/api/ParleyApi';

// The global Chat (LibreChat balance-lookup) kill-switch (super-admin,
// Configuration page → System parameters). Off hides the Chat sidebar item
// AND blocks the /chat route, for both super_admin and support; the
// underlying /v1/admin/chat/balance and /v1/admin/chat/transactions
// endpoints already 404 the same way when this is off, so this flag only
// controls whether the UI surfaces the feature at all. GET
// /v1/admin/chat/config is readable by both roles, same as the Knowledge
// Base flag's GET.
//
// Cached at MODULE level like useKnowledgeBaseFlag, so the router guard and
// the sidebar share one request rather than each firing their own.
let cached: Promise<boolean> | null = null;

/**
 * Resolves the flag, sharing one request across every caller. Fails closed:
 * an unreachable or erroring endpoint reads as "off".
 */
export function fetchChatBalanceCheckEnabled(): Promise<boolean> {
  if (!cached) {
    cached = getChatBalanceCheckEnabled().catch(() => {
      // Fail closed for THIS read, but drop the cache so the next navigation
      // retries. Caching the rejection would hide the nav item for the rest
      // of the session over one transient error.
      cached = null;
      return false;
    });
  }
  return cached;
}

/**
 * Drops the cached answer so the next read re-fetches. Called after a
 * super-admin flips the switch, so the sidebar/route reflect it without a
 * full page reload — and by tests, which must not inherit each other's cache.
 */
export function resetChatBalanceCheckFlagCache(): void {
  cached = null;
}

/**
 * Component-facing wrapper. `loading` starts true so the sidebar/route render
 * as off until the answer arrives, rather than flashing the item and then
 * pulling it away.
 */
export function useChatBalanceCheckFlag() {
  const chatBalanceCheckEnabled = ref(false);
  const loading = ref(true);

  async function loadChatBalanceCheckFlag() {
    try {
      chatBalanceCheckEnabled.value = await fetchChatBalanceCheckEnabled();
    } finally {
      loading.value = false;
    }
  }

  return { chatBalanceCheckEnabled, loading, loadChatBalanceCheckFlag };
}
